import * as React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    Snackbar,
    Alert,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Checkbox,
    Chip,
    IconButton,
    Tooltip,
    Paper,
    FormControl,
    InputLabel,
    Select,
    Pagination,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {
    Backspace,
    BadgeTwoTone,
    DeleteForever,
    DeleteTwoTone,
    Upgrade,
    LocationOn,
    Event,
    CheckCircle,
} from '@mui/icons-material';
import CertificateIcon from '@mui/icons-material/WorkspacePremium';
import AnimateButton from 'ui-component/extended/AnimateButton';

// import SwipeableViews from 'react-swipeable-views';
// import { useTheme } from '@mui/material/styles';

import PropTypes from 'prop-types';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import SendEmail from 'Components/SendEmail';
import { set } from 'store';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role='tabpanel'
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }} component='div'>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        'aria-controls': `full-width-tabpanel-${index}`,
    };
}

export default function VerificationPage() {
    // const theme = useTheme();
    const [value, setValue] = React.useState(0);
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // const handleChangeIndex = index => {
    //     setValue(index);
    // };
    const { eventId, role } = useParams();
    const [search, setSearch] = React.useState('');
    const { data, loading, error } = useQuery(GET_ME);
    const [tags, setTags] = React.useState([]);
    const { data: featureData, loading: featureLoading } = useQuery(GET_VERIFICATION_FEATURE, {
        variables: { eventId, role },
    });

    // Get form data to check features
    const { data: formData } = useQuery(GET_VERIFIED_DATA, {
        variables: {
            eventId: eventId,
            role: role,
            status: 'accept',
            conditions: {
                text: '',
                orderBy: 'created',
                order: 'desc',
            },
        },
        skip: !eventId || !role,
    });

    // Separate checks for Conference Choices and emailVerification features
    const hasConferenceChoices = formData?.form?.features?.includes('ConferenceChoices');
    const hasEmailVerification = formData?.form?.features?.includes('emailVerification');
    const isConferenceChoicesEnabled = hasConferenceChoices && hasEmailVerification;
    React.useEffect(() => {
        if (featureData && featureData.mailVerificationFeature) {
            let parsedData = [];
            try {
                parsedData = JSON.parse(featureData.mailVerificationFeature);
            } catch (err) {
                console.error('Error parsing the data:', err);
            }
            if (Array.isArray(parsedData)) {
                setTags(parsedData);
            }
        }
    }, [featureData]);
    if (featureLoading) return <LinearProgress />;

    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography>{error.message}</Typography>;
    }

    return (
        <Stack spacing={2}>
            {/* Event Choices Insights - only show if feature is enabled */}
            {data?.form?.features?.includes('ConferenceChoices') &&
                data?.form?.features?.includes('emailVerification') && (
                    <Paper
                        sx={{
                            p: 3,
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                            borderRadius: 3,
                            border: '2px solid',
                            borderColor: 'primary.light',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        }}
                    >
                        <Typography
                            variant='h5'
                            sx={{
                                mb: 3,
                                fontWeight: 700,
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            📊 Event Choices Insights
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                                    <Typography variant='h4' color='primary.main'>
                                        {data?.total || 0}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Total Registrations
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                                    <Typography variant='h4' color='success.main'>
                                        {data?.verifiedApplicants?.filter(app => app.status === 'accept').length || 0}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Accepted
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                                    <Typography variant='h4' color='warning.main'>
                                        {data?.verifiedApplicants?.filter(app => app.status === 'pending').length || 0}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Pending
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                                    <Typography variant='h4' color='error.main'>
                                        {data?.verifiedApplicants?.filter(app => app.status === 'reject').length || 0}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Rejected
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>
                )}

            {/* Event Choices Analytics - only show if feature is enabled */}
            {isConferenceChoicesEnabled && (
                <Paper
                    sx={{
                        p: 3,
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor: 'primary.light',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    }}
                >
                    <Typography
                        variant='h5'
                        sx={{
                            mb: 3,
                            fontWeight: 700,
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        🎯 Event Choices Analytics
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Workshop Analytics */}
                        <Grid item xs={12} md={4}>
                            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                                📚 Total Sessions:{' '}
                                {(() => {
                                    const workshops = new Set();
                                    formData?.verifiedApplicants?.forEach(applicant => {
                                        const parsedData = JSON.parse(applicant.data || '{}');
                                        if (parsedData.Workshops) {
                                            workshops.add(parsedData.Workshops);
                                        }
                                    });
                                    return workshops.size;
                                })()}
                            </Typography>
                            {(() => {
                                const workshops = {};
                                formData?.verifiedApplicants?.forEach(applicant => {
                                    const parsedData = JSON.parse(applicant.data || '{}');
                                    if (parsedData.Workshops) {
                                        const workshop = parsedData.Workshops;
                                        if (!workshops[workshop]) {
                                            workshops[workshop] = { total: 0, confirmed: 0, pending: 0 };
                                        }
                                        workshops[workshop].total++;
                                        if (applicant.is_choice_final) {
                                            workshops[workshop].confirmed++;
                                        } else {
                                            workshops[workshop].pending++;
                                        }
                                    }
                                });
                                return Object.entries(workshops).map(([name, stats]) => (
                                    <Paper key={name} sx={{ p: 2, mb: 2, bgcolor: 'white', borderRadius: 2 }}>
                                        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                                            {name}
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Typography variant='body2'>Total Registration: {stats.total}</Typography>
                                            <Typography variant='body2' color='success.main'>
                                                Confirmed Registration: {stats.confirmed}
                                            </Typography>
                                            <Typography variant='body2' color='warning.main'>
                                                Pending Confirmation: {stats.pending}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ));
                            })()}
                        </Grid>

                        {/* Region Analytics */}
                        <Grid item xs={12} md={4}>
                            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                                🌍 All Unique Regions:{' '}
                                {(() => {
                                    const regions = new Set();
                                    formData?.verifiedApplicants?.forEach(applicant => {
                                        const parsedData = JSON.parse(applicant.data || '{}');
                                        if (parsedData.Region) {
                                            regions.add(parsedData.Region);
                                        }
                                    });
                                    return regions.size;
                                })()}
                            </Typography>
                            {(() => {
                                const regions = {};
                                formData?.verifiedApplicants?.forEach(applicant => {
                                    const parsedData = JSON.parse(applicant.data || '{}');
                                    if (parsedData.Region) {
                                        const region = parsedData.Region;
                                        if (!regions[region]) {
                                            regions[region] = { total: 0, confirmed: 0, pending: 0 };
                                        }
                                        regions[region].total++;
                                        if (applicant.is_choice_final) {
                                            regions[region].confirmed++;
                                        } else {
                                            regions[region].pending++;
                                        }
                                    }
                                });
                                return Object.entries(regions).map(([name, stats]) => (
                                    <Paper key={name} sx={{ p: 2, mb: 2, bgcolor: 'white', borderRadius: 2 }}>
                                        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                                            {name}
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Typography variant='body2'>Total Registration: {stats.total}</Typography>
                                            <Typography variant='body2' color='success.main'>
                                                Confirmed Registration: {stats.confirmed}
                                            </Typography>
                                            <Typography variant='body2' color='warning.main'>
                                                Pending Confirmation: {stats.pending}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ));
                            })()}
                        </Grid>

                        {/* Venue Analytics */}
                        <Grid item xs={12} md={4}>
                            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                                🏢 Total Venues:{' '}
                                {(() => {
                                    const venues = new Set();
                                    formData?.verifiedApplicants?.forEach(applicant => {
                                        if (applicant.venue_location) {
                                            venues.add(applicant.venue_location);
                                        }
                                    });
                                    return venues.size;
                                })()}
                            </Typography>
                            {(() => {
                                const venues = {};
                                formData?.verifiedApplicants?.forEach(applicant => {
                                    if (applicant.venue_location) {
                                        const venue = applicant.venue_location;
                                        if (!venues[venue]) {
                                            venues[venue] = {
                                                total: 0,
                                                startDate: applicant.event_date_from,
                                                endDate: applicant.event_date_to,
                                            };
                                        }
                                        venues[venue].total++;
                                    }
                                });
                                return Object.entries(venues).map(([name, stats]) => (
                                    <Paper key={name} sx={{ p: 2, mb: 2, bgcolor: 'white', borderRadius: 2 }}>
                                        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                                            {name}
                                        </Typography>
                                        <Typography variant='body2' sx={{ mb: 1 }}>
                                            Total Registration: {stats.total}
                                        </Typography>
                                        {stats.startDate && stats.endDate && (
                                            <Typography variant='body2' color='text.secondary'>
                                                {(() => {
                                                    try {
                                                        const startDate = new Date(parseInt(stats.startDate));
                                                        const endDate = new Date(parseInt(stats.endDate));
                                                        return `${format(startDate, 'dd-MMM-yyyy')} - ${format(
                                                            endDate,
                                                            'dd-MMM-yyyy'
                                                        )}`;
                                                    } catch (error) {
                                                        return 'Date format error';
                                                    }
                                                })()}
                                            </Typography>
                                        )}
                                    </Paper>
                                ));
                            })()}
                        </Grid>
                    </Grid>
                </Paper>
            )}

            <MainCard>
                <Grid spacing={gridSpacing} container={true}>
                    <Grid item xs={12}>
                        <Stack
                            direction={'row'}
                            justifyContent='space-between'
                            sx={{ bgcolor: 'background.paper', width: '100%', mb: 2 }}
                        >
                            <Typography variant='h1' textAlign={'center'}>
                                Verify {role} Data
                            </Typography>
                            {data?.me?.permissions?.includes('find-' + role) && <SearchBox onSearch={setSearch} />}
                        </Stack>

                        <Box sx={{ bgcolor: 'background.paper', width: '100%' }}>
                            <AppBar position='relative' color='secondary'>
                                <Tabs
                                    sx={{
                                        '& .Mui-selected': { background: '#05344f', color: 'white' },
                                        background: 'white',
                                        color: 'black',
                                    }}
                                    value={value}
                                    onChange={handleChange}
                                    indicatorColor='secondary'
                                    textColor='inherit'
                                    variant='fullWidth'
                                    aria-label='full width tabs example'
                                >
                                    <Tab
                                        sx={{ '& .Mui-selected': { background: '#05344f' }, border: '1px solid black' }}
                                        label='Accepted Registration'
                                        {...a11yProps(0)}
                                    />
                                    <Tab
                                        sx={{ '& .Mui-selected': { background: '#05344f' }, border: '1px solid black' }}
                                        label='Pending Registration'
                                        {...a11yProps(1)}
                                    />
                                    <Tab
                                        sx={{ '& .Mui-selected': { background: '#05344f' }, border: '1px solid black' }}
                                        label='Rejected Registration'
                                        {...a11yProps(2)}
                                    />
                                </Tabs>
                            </AppBar>
                            {/* <SwipeableViews
                                axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                                index={value}
                                onChangeIndex={handleChangeIndex}
                            >
                                <TabPanel value={value} index={0} dir={theme.direction}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status='accept'
                                        permissions={data.me?.permissions?.split(',') || []}
                                        setSnackbar={setSnackbar}
                                    />
                                </TabPanel>
                                <TabPanel value={value} index={1} dir={theme.direction}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status='pending'
                                        permissions={data.me?.permissions?.split(',') || []}
                                        setSnackbar={setSnackbar}
                                    />
                                </TabPanel>
                                <TabPanel value={value} index={2} dir={theme.direction}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status='reject'
                                        permissions={data.me?.permissions?.split(',') || []}
                                        setSnackbar={setSnackbar}
                                    />
                                </TabPanel>
                            </SwipeableViews> */}
                            <>
                                <TabPanel value={value} index={0}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status='accept'
                                        permissions={data.me?.permissions?.split(',') || []}
                                        setSnackbar={setSnackbar}
                                    />
                                </TabPanel>

                                <TabPanel value={value} index={1}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status='pending'
                                        permissions={data.me?.permissions?.split(',') || []}
                                        setSnackbar={setSnackbar}
                                    />
                                </TabPanel>

                                <TabPanel value={value} index={2}>
                                    <DataTable
                                        {...{ eventId, search, role }}
                                        tags={tags}
                                        status='reject'
                                        permissions={data.me?.permissions?.split(',') || []}
                                        setSnackbar={setSnackbar}
                                    />
                                </TabPanel>
                            </>
                        </Box>
                    </Grid>
                </Grid>
            </MainCard>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
}

function SearchBox({ onSearch }) {
    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        const t = setTimeout(() => onSearch(search), 500);
        return () => clearTimeout(t);
    }, [search]);
    return (
        <TextField
            sx={{ width: 600 }}
            label='Search here...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
                autoComplete: 'new-password',
                endAdornment: (
                    <InputAdornment position='end'>
                        {search && (
                            <IconButton onClick={() => setSearch('')}>
                                <Backspace />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
}

function DataTable({ eventId, role, search = '', permissions, tags, status, setSnackbar }) {
    const [workshopFilter, setWorkshopFilter] = React.useState('');
    const [regionFilter, setRegionFilter] = React.useState('');
    const [venueFilter, setVenueFilter] = React.useState('');
    const [assignVenueOpen, setAssignVenueOpen] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [orderBy, setOrderBy] = React.useState('created');
    const [order, setOrder] = React.useState('desc');
    const [sortBy, setSortBy] = React.useState('created');
    const [sortOrder, setSortOrder] = React.useState('desc');

    const { data, loading, error, refetch } = useQuery(GET_VERIFIED_DATA, {
        variables: {
            eventId,
            role,
            status,
            conditions: {
                limit: pageSize === -1 ? -1 : pageSize,
                offset: pageSize === -1 ? 0 : page * pageSize,
                text: search,
                orderBy: 'created',
                order: 'desc',
                workshopFilter,
                regionFilter,
                venueFilter,
            },
        },
    });

    // Reset page to 0 when filters change (but not when page itself changes)
    React.useEffect(() => {
        setPage(0);
    }, [search, workshopFilter, regionFilter, venueFilter, pageSize]);

    // Refetch when pagination/filters change
    React.useEffect(() => {
        refetch({
            eventId,
            role,
            status,
            conditions: {
                limit: pageSize === -1 ? -1 : pageSize,
                offset: pageSize === -1 ? 0 : page * pageSize,
                text: search,
                orderBy: 'created',
                order: 'desc',
                workshopFilter,
                regionFilter,
                venueFilter,
            },
        });
    }, [page, pageSize, search, workshopFilter, regionFilter, venueFilter, refetch, eventId, role, status]);
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const [currentAction, setCurrentAction] = React.useState({});
    const [noOfParticipant, setNoOfParticipant] = React.useState(0);
    const [paidSeats, setPaidSeats] = React.useState(0);
    const [freeSeats, setFreeSeats] = React.useState(0);
    const [discount, setDiscount] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(false);
    const [modalMessage, setModalMessage] = React.useState('');
    const [upgradeParticipant, setUpgradeParticipant] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);

    // Conference Choices state
    const [venueLocation, setVenueLocation] = React.useState('');
    const [eventDateFrom, setEventDateFrom] = React.useState('');
    const [eventDateTo, setEventDateTo] = React.useState('');
    const [isChoiceFinal, setIsChoiceFinal] = React.useState(false);
    const [finalChoice, setFinalChoice] = React.useState('');

    // Edit state for individual fields
    const [editValues, setEditValues] = React.useState({});

    // Separate checks for Conference Choices and emailVerification features
    const hasConferenceChoices = data?.form?.features?.includes('ConferenceChoices');
    const hasEmailVerification = data?.form?.features?.includes('emailVerification');
    const isConferenceChoicesEnabled = hasConferenceChoices && hasEmailVerification;

    // Debug logging
    console.log('VerificationPage Debug:', {
        isConferenceChoicesEnabled,
        features: data?.form?.features,
        verifiedApplicants: data?.verifiedApplicants?.length,
        total: data?.total,
    });

    // Set default final choice when modal opens for pending tab
    React.useEffect(() => {
        if (assignVenueOpen && status === 'pending' && isConferenceChoicesEnabled && selectedIds.length > 0) {
            // Get the first selected row's workshop as default
            const firstSelectedRow = data?.verifiedApplicants?.find(row =>
                selectedIds.some(selected => selected.id === row.id)
            );
            if (firstSelectedRow) {
                const parsedData = JSON.parse(firstSelectedRow.data || '{}');
                if (parsedData.Workshops && !finalChoice) {
                    setFinalChoice(parsedData.Workshops);
                }
            }
        }
    }, [assignVenueOpen, status, isConferenceChoicesEnabled, selectedIds, data?.verifiedApplicants, finalChoice]);

    const handleAcceptReject = (userData, value) => {
        console.log(userData);
        setUpgradeParticipant(null);

        setCurrentAction({
            id: userData.id,
            value,
            no_of_participant: userData?.no_of_participant || 0,
            freeSeats: userData?.freeSeats || 0,
            paidSeats: userData?.paidSeats || 0,
            discount: userData?.discount || 0,
        });
        if (value === 'accept') {
            if (tags?.includes('tokenLink')) {
                console.log(userData?.no_of_participant);
                setNoOfParticipant(userData?.no_of_participant);
                setModalMessage(
                    <>
                        You are about to accept this application. Please specify the details below:
                        <br />
                        - **No of Participants**: Enter the total number of participants that will be allowed.
                        <br />
                        - **Paid Seats**: Enter the number of seats that will be paid for.
                        <br />
                        - **Free Seats**: Enter the number of seats that will be provided for free.
                        <br />- **Discount (if applicable)**: Enter any discount that may apply to the registration.
                    </>
                );
            } else {
                setModalMessage(
                    <>
                        Are you sure you wish to <b>Accept</b> this application?
                        <br />
                        <br />
                        An <b>Email</b> will be sent to the attendee upon <b>Acceptance</b>.
                    </>
                );
            }
        } else if (value === 'reject') {
            if (tags?.includes('tokenLink')) {
                setModalMessage(
                    <>
                        Are you sure you wish to <b>Reject</b> this application?
                        <br />
                        <br />
                        An <b>Email</b> will be sent to the attendee upon <b>Rejection</b>.
                    </>
                );
            } else {
                setModalMessage(
                    <>
                        Are you sure you wish to <b>Reject</b> this application?
                        <br />
                        <br />
                        An <b>Email</b> will be sent to the attendee upon <b>Rejection</b>.
                    </>
                );
            }
        } else if (value === 'upgrade') {
            setNoOfParticipant(userData?.no_of_participant);
            setModalMessage(
                <>
                    You are about to upgrade the registration limit for this application. Please specify the details
                    below:
                    <br />
                    - **Add No of Participants**: Enter the number of additional participants to authorize.
                    <br />
                    - **Paid Seats**: Enter the number of seats that will be paid for.
                    <br />
                    - **Free Seats**: Enter the number of seats that will be provided for free.
                    <br />- **Discount (if applicable)**: Enter any discount that may apply to the registration.
                </>
            );
        }
        setIsModalOpen(true);
    };

    const handleNoofParticipate = (value, action) => {
        if (action === 'accept') {
            setNoOfParticipant(value);
        } else if (action === 'upgrade') {
            setUpgradeParticipant(value);
        } else if (action === 'reject') {
            setNoOfParticipant(value);
        }
    };

    const confirmAction = () => {
        const totalSeats = parseInt(paidSeats || 0) + parseInt(freeSeats || 0);
        const currentParticipants =
            currentAction.value === 'upgrade' ? parseInt(upgradeParticipant) : parseInt(noOfParticipant);
        const currentNewParticipants =
            currentAction.value === 'upgrade'
                ? parseInt(upgradeParticipant) + parseInt(noOfParticipant)
                : parseInt(noOfParticipant);
        const currentfreeSeats =
            currentAction.value === 'upgrade'
                ? parseInt(freeSeats || 0) + parseInt(currentAction.freeSeats || 0)
                : parseInt(freeSeats || 0);
        const currentpaidSeats =
            currentAction.value === 'upgrade'
                ? parseInt(paidSeats || 0) + parseInt(currentAction.paidSeats || 0)
                : parseInt(paidSeats || 0);
        const currentDiscount =
            currentAction.value === 'upgrade'
                ? parseInt(discount || 0) + parseInt(currentAction.discount || 0)
                : parseInt(discount || 0);

        // Validate the sum of paid and free seats
        if (totalSeats !== parseInt(currentParticipants)) {
            setSnackbar({
                open: true,
                message: `The sum of Paid Seats and Free Seats must equal No of Participants (${currentParticipants}).`,
                severity: 'error',
            });
            return;
        }

        setIsLoading(true);

        // Prepare variables for the mutation
        const variables = {
            eventId,
            role,
            id: currentAction.id,
            field:
                currentAction.value === 'accept' ? 'status' : currentAction.value === 'reject' ? 'status' : 'upgrade',
            value: currentAction.value,
            no_of_participant: currentNewParticipants.toString(),
            ...(tags?.includes('tokenLink') && {
                paidSeats: currentpaidSeats.toString(),
                freeSeats: currentfreeSeats.toString(),
                discount: currentDiscount.toString(),
            }),
        };

        updateApplicantStatusandMail({ variables })
            .then(response => {
                setSnackbar({
                    open: true,
                    message: response?.data?.result || 'Updated successfully.',
                    severity: 'success',
                });
                setIsLoading(false);
                setIsModalOpen(false);
                refetch({
                    eventId: eventId || '7493635113',
                    role: role || 'participant',
                    status,
                    conditions: {
                        limit: pageSize === -1 ? -1 : pageSize,
                        offset: pageSize === -1 ? 0 : page * pageSize,
                        text: search,
                        orderBy: 'created',
                        order: 'desc',
                        workshopFilter,
                        regionFilter,
                        venueFilter,
                    },
                }); // Assuming you want to refetch data after the update
            })
            .catch(error => {
                setSnackbar({
                    open: true,
                    message: error.message || "Something went wrong. Couldn't perform the action.",
                    severity: 'error',
                });
                setIsLoading(false);
            });
    };
    const [updateApplicantStatusandMail] = useMutation(UPDATE_APPLICANT_STATUS_AND_MAIL, {
        onCompleted: () =>
            refetch({
                eventId: eventId || '7493635113',
                role: role || 'participant',
                status,
                conditions: {
                    limit: pageSize === -1 ? -1 : pageSize,
                    offset: pageSize === -1 ? 0 : page * pageSize,
                    text: search,
                    orderBy: 'created',
                    order: 'desc',
                    workshopFilter,
                    regionFilter,
                    venueFilter,
                },
            }),
        onError: e => {
            refetch({
                eventId: eventId || '7493635113',
                role: role,
                status,
                conditions: {
                    limit: pageSize === -1 ? -1 : pageSize,
                    offset: pageSize === -1 ? 0 : page * pageSize,
                    text: search,
                    orderBy: 'created',
                    order: 'desc',
                    workshopFilter,
                    regionFilter,
                    venueFilter,
                },
            });
            console.log(e);
        },
    });
    const [updateField] = useMutation(UPDATE_APPLICANT_FIELD, {
        onCompleted: () =>
            refetch({
                eventId: eventId || '7493635113',
                role: role || 'participant',
                status,
                conditions: {
                    limit: pageSize === -1 ? -1 : pageSize,
                    offset: pageSize === -1 ? 0 : page * pageSize,
                    text: search,
                    orderBy: 'created',
                    order: 'desc',
                    workshopFilter,
                    regionFilter,
                    venueFilter,
                },
            }),
        onError: e => console.log(e),
    });
    const [doDelete] = useMutation(DELETE_APPLICANT, {
        onCompleted: () =>
            refetch({
                eventId: eventId || '7493635113',
                role: role || 'participant',
                status,
                conditions: {
                    limit: pageSize === -1 ? -1 : pageSize,
                    offset: pageSize === -1 ? 0 : page * pageSize,
                    text: search,
                    orderBy: 'created',
                    order: 'desc',
                    workshopFilter,
                    regionFilter,
                    venueFilter,
                },
            }),
    });
    const [assignVenue] = useMutation(ASSIGN_VENUE, {
        onCompleted: () =>
            refetch({
                eventId: eventId || '7493635113',
                role: role || 'participant',
                status,
                conditions: {
                    limit: pageSize === -1 ? -1 : pageSize,
                    offset: pageSize === -1 ? 0 : page * pageSize,
                    text: search,
                    orderBy: 'created',
                    order: 'desc',
                    workshopFilter,
                    regionFilter,
                    venueFilter,
                },
            }),
    });

    const handleAssignVenue = async () => {
        if (!isChoiceFinal) {
            setSnackbar({
                open: true,
                message: 'Please confirm that you want to finalize the venue assignment',
                severity: 'error',
            });
            return;
        }

        try {
            const response = await assignVenue({
                variables: {
                    eventId,
                    role,
                    ids: selectedIds.map(id => id.id),
                    venueLocation,
                    eventDateFrom,
                    eventDateTo,
                    finalChoice,
                    status: status === 'pending' && isConferenceChoicesEnabled ? 'accept' : 'accept',
                },
            });

            setSnackbar({
                open: true,
                message:
                    response.data.assignVenue.message ||
                    (status === 'pending' && isConferenceChoicesEnabled
                        ? 'Venue and workshop assigned successfully'
                        : 'Venue assigned successfully'),
                severity: 'success',
            });

            setAssignVenueOpen(false);
            setSelectedIds([]);
            setVenueLocation('');
            setEventDateFrom('');
            setEventDateTo('');
            setIsChoiceFinal(false);
            setFinalChoice('');
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to assign venue',
                severity: 'error',
            });
        }
    };

    // Get unique workshops and regions for filtering
    const uniqueWorkshops = React.useMemo(() => {
        if (!data?.verifiedApplicants) return [];
        const workshops = new Set();
        data.verifiedApplicants.forEach(applicant => {
            const parsedData = JSON.parse(applicant.data || '{}');
            if (parsedData.Workshops) {
                workshops.add(parsedData.Workshops);
            }
        });
        return Array.from(workshops);
    }, [data?.verifiedApplicants]);

    const uniqueRegions = React.useMemo(() => {
        if (!data?.verifiedApplicants) return [];
        const regions = new Set();
        data.verifiedApplicants.forEach(applicant => {
            const parsedData = JSON.parse(applicant.data || '{}');
            if (parsedData.Region) {
                regions.add(parsedData.Region);
            }
        });
        return Array.from(regions);
    }, [data?.verifiedApplicants]);

    // Get unique venues for filtering
    const uniqueVenues = React.useMemo(() => {
        if (!data?.verifiedApplicants) return [];
        const venues = new Set();
        data.verifiedApplicants.forEach(applicant => {
            if (applicant.venue_location) {
                venues.add(applicant.venue_location);
            }
        });
        return Array.from(venues);
    }, [data?.verifiedApplicants]);

    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography color='error'>{error.message}</Typography>;
    }
    return (
        <Stack spacing={2}>
            {/* Filters Row */}
            <Box>
                <Stack direction='row' spacing={2} flexWrap='wrap'>
                    {/* Filter dropdowns for pending tab */}
                    {status === 'pending' && (
                        <>
                            <TextField
                                select
                                label='Filter by Workshop'
                                value={workshopFilter}
                                onChange={e => setWorkshopFilter(e.target.value)}
                                sx={{ minWidth: 200 }}
                                size='small'
                            >
                                <MenuItem value=''>All Workshops</MenuItem>
                                {uniqueWorkshops.map(workshop => (
                                    <MenuItem key={workshop} value={workshop}>
                                        {workshop}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label='Filter by Region'
                                value={regionFilter}
                                onChange={e => setRegionFilter(e.target.value)}
                                sx={{ minWidth: 200 }}
                                size='small'
                            >
                                <MenuItem value=''>All Regions</MenuItem>
                                {uniqueRegions.map(region => (
                                    <MenuItem key={region} value={region}>
                                        {region}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}

                    {/* Conference Choices Filtering for other tabs */}
                    {isConferenceChoicesEnabled && status !== 'pending' && (
                        <>
                            <TextField
                                select
                                label='Filter by Workshop'
                                value={workshopFilter}
                                onChange={e => setWorkshopFilter(e.target.value)}
                                sx={{ minWidth: 200 }}
                                size='small'
                            >
                                <MenuItem value=''>All Workshops</MenuItem>
                                {uniqueWorkshops.map(workshop => (
                                    <MenuItem key={workshop} value={workshop}>
                                        {workshop}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label='Filter by Region'
                                value={regionFilter}
                                onChange={e => setRegionFilter(e.target.value)}
                                sx={{ minWidth: 200 }}
                                size='small'
                            >
                                <MenuItem value=''>All Regions</MenuItem>
                                {uniqueRegions.map(region => (
                                    <MenuItem key={region} value={region}>
                                        {region}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label='Filter by Venue'
                                value={venueFilter}
                                onChange={e => setVenueFilter(e.target.value)}
                                sx={{ minWidth: 200 }}
                                size='small'
                            >
                                <MenuItem value=''>All Venues</MenuItem>
                                {uniqueVenues.map(venue => (
                                    <MenuItem key={venue} value={venue}>
                                        {venue}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}
                </Stack>
            </Box>

            {/* Action Buttons Row */}
            <Box>
                <Stack direction='row' spacing={2} flexWrap='wrap'>
                    <AnimateButton>
                        <Button
                            variant='contained'
                            disabled={loading}
                            onClick={() =>
                                refetch({
                                    eventId: eventId || '7493635113',
                                    role: role || 'participant',
                                    status,
                                    conditions: {
                                        limit: pageSize === -1 ? -1 : pageSize,
                                        offset: pageSize === -1 ? 0 : page * pageSize,
                                        text: search,
                                        orderBy: 'created',
                                        order: 'desc',
                                        workshopFilter,
                                        regionFilter,
                                        venueFilter,
                                    },
                                })
                            }
                        >
                            Refresh
                        </Button>
                    </AnimateButton>

                    {permissions.includes('admin') && permissions.includes('bulk-email') ? (
                        <SendEmail {...{ selectedIds, eventId, role }} />
                    ) : (
                        <div />
                    )}

                    {permissions.includes('admin') && selectedIds.length > 0 && (
                        <WhatsAppBulkMessage {...{ selectedIds, eventId, role }} />
                    )}

                    {/* Assign Venue button - only shows in pending tab when rows are selected */}
                    {status === 'pending' && selectedIds.length > 0 && (
                        <Button
                            variant='contained'
                            color='secondary'
                            startIcon={<Event />}
                            onClick={() => setAssignVenueOpen(true)}
                        >
                            Assign Venue & Location ({selectedIds.length})
                        </Button>
                    )}
                </Stack>
            </Box>
            <Box>
                <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} aria-labelledby='message-dialog-title'>
                    <DialogTitle id='message-dialog-title' sx={{ fontSize: 24 }}>
                        {!!currentAction.id ? 'Confirm Action' : 'Message'}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>{modalMessage}</DialogContentText>
                        {tags?.includes('tokenLink') && currentAction.value === 'accept' && (
                            <Stack spacing={2} sx={{ m: 2 }}>
                                <TextField
                                    type='number'
                                    label='No of Participants'
                                    value={noOfParticipant}
                                    onChange={e => handleNoofParticipate(e.target.value, currentAction.value)}
                                />
                                <TextField
                                    type='number'
                                    label='Paid Seats'
                                    value={paidSeats}
                                    onChange={e => setPaidSeats(e.target.value)}
                                />
                                <TextField
                                    type='number'
                                    label='Free Seats'
                                    value={freeSeats}
                                    onChange={e => setFreeSeats(e.target.value)}
                                />
                                <TextField
                                    type='number'
                                    label='Discount (if applicable)'
                                    value={discount}
                                    onChange={e => setDiscount(e.target.value)}
                                />
                            </Stack>
                        )}
                        {tags?.includes('tokenLink') && currentAction.value === 'upgrade' && (
                            <Stack spacing={2} sx={{ m: 2 }}>
                                <Typography variant='body2'>
                                    Current participant limit: {currentAction.no_of_participant}. Please specify the
                                    number of additional attendees to authorize:
                                </Typography>
                                <TextField
                                    type='number'
                                    label='Add No of Participants'
                                    value={upgradeParticipant}
                                    onChange={e => handleNoofParticipate(e.target.value, currentAction.value)}
                                />
                                <TextField
                                    type='number'
                                    label='Paid Seats'
                                    value={paidSeats}
                                    onChange={e => setPaidSeats(e.target.value)}
                                />
                                <TextField
                                    type='number'
                                    label='Free Seats'
                                    value={freeSeats}
                                    onChange={e => setFreeSeats(e.target.value)}
                                />
                                <TextField
                                    type='number'
                                    label='Discount (if applicable)'
                                    value={discount}
                                    onChange={e => setDiscount(e.target.value)}
                                />
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsModalOpen(false)} variant='outlined' color='error'>
                            Close
                        </Button>
                        {!!currentAction.id && (
                            <Button onClick={confirmAction} variant='contained' color='success'>
                                {isLoading ? <CircularProgress color='primary' size={20} /> : 'Confirm'}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                {/* Enhanced Assign Venue Modal */}
                <Dialog open={assignVenueOpen} onClose={() => setAssignVenueOpen(false)} maxWidth='lg' fullWidth>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Event color='primary' />
                            <Typography variant='h6' component='div'>
                                {isConferenceChoicesEnabled
                                    ? 'Assign Venue & Location for Selected Attendees'
                                    : 'Assign Venue and Location'}
                            </Typography>
                        </Box>
                        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                            You are about to assign venue and location details to {selectedIds.length} selected
                            attendee(s)
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Stack spacing={3}>
                            {isConferenceChoicesEnabled && (
                                <Alert severity='info' sx={{ borderRadius: 2 }}>
                                    <Typography variant='body2'>
                                        You are assigning venue and location information for {selectedIds.length}{' '}
                                        selected attendee(s). This will update their workshop choice and venue details.
                                    </Typography>
                                </Alert>
                            )}

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label='Venue Location'
                                        value={venueLocation}
                                        onChange={e => setVenueLocation(e.target.value)}
                                        placeholder='Enter venue location'
                                        required
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label='Final Choice (Workshop)'
                                        value={finalChoice}
                                        onChange={e => setFinalChoice(e.target.value)}
                                        required
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    >
                                        {uniqueWorkshops.map(workshop => (
                                            <MenuItem key={workshop} value={workshop}>
                                                {workshop}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type='date'
                                        label='Event Start Date'
                                        value={eventDateFrom}
                                        onChange={e => setEventDateFrom(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type='date'
                                        label='Event End Date'
                                        value={eventDateTo}
                                        onChange={e => setEventDateTo(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        error={
                                            eventDateTo &&
                                            eventDateFrom &&
                                            new Date(eventDateTo) < new Date(eventDateFrom)
                                        }
                                        helperText={
                                            eventDateTo &&
                                            eventDateFrom &&
                                            new Date(eventDateTo) < new Date(eventDateFrom)
                                                ? 'End date must be on or after start date'
                                                : ''
                                        }
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>

                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <input
                                        type='checkbox'
                                        checked={isChoiceFinal}
                                        onChange={e => setIsChoiceFinal(e.target.checked)}
                                        style={{ marginTop: 4 }}
                                    />
                                    <Box>
                                        <Typography variant='body1' fontWeight='medium' gutterBottom>
                                            Final Confirmation Required
                                        </Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            Are you sure to finalize all selected attendees to assign above venue and
                                            date? This action will update the workshop choice and venue details for all
                                            selected participants.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 1 }}>
                        <Button onClick={() => setAssignVenueOpen(false)} variant='outlined' sx={{ borderRadius: 2 }}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={handleAssignVenue}
                            disabled={
                                !venueLocation ||
                                !eventDateFrom ||
                                !eventDateTo ||
                                !finalChoice ||
                                !isChoiceFinal ||
                                (eventDateTo && eventDateFrom && new Date(eventDateTo) < new Date(eventDateFrom))
                            }
                            sx={{ borderRadius: 2, minWidth: 160 }}
                        >
                            {isConferenceChoicesEnabled ? 'Assign Venue & Location' : 'Assign Venue'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Enhanced Table Component */}
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {/* Checkbox column for selection */}
                                    {(permissions.includes('admin') || permissions.includes('email')) && (
                                        <TableCell padding='checkbox'>
                                            <Checkbox
                                                indeterminate={
                                                    selectedIds.length > 0 &&
                                                    selectedIds.length < (data?.verifiedApplicants?.length || 0)
                                                }
                                                checked={
                                                    (data?.verifiedApplicants?.length || 0) > 0 &&
                                                    selectedIds.length === (data?.verifiedApplicants?.length || 0)
                                                }
                                                onChange={event => {
                                                    if (event.target.checked) {
                                                        const allIds =
                                                            data?.verifiedApplicants?.map(applicant => {
                                                                const parsedData = JSON.parse(applicant.data || '{}');
                                                                return {
                                                                    id: applicant.id,
                                                                    email: parsedData.email || parsedData.Email,
                                                                };
                                                            }) || [];
                                                        setSelectedIds(allIds);
                                                    } else {
                                                        setSelectedIds([]);
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    )}

                                    {/* Actions column */}
                                    <TableCell>Actions</TableCell>

                                    {/* ID column */}
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'id'}
                                            direction={sortBy === 'id' ? sortOrder : 'asc'}
                                            onClick={() => {
                                                const newOrder =
                                                    sortBy === 'id' && sortOrder === 'asc' ? 'desc' : 'asc';
                                                setSortBy('id');
                                                setSortOrder(newOrder);
                                                // Trigger refetch with new sort order
                                                refetch({
                                                    eventId,
                                                    role,
                                                    status,
                                                    conditions: {
                                                        limit: pageSize === -1 ? -1 : pageSize,
                                                        offset: pageSize === -1 ? 0 : page * pageSize,
                                                        text: search,
                                                        orderBy: 'id',
                                                        order: newOrder,
                                                        workshopFilter,
                                                        regionFilter,
                                                    },
                                                });
                                            }}
                                        >
                                            ID
                                        </TableSortLabel>
                                    </TableCell>

                                    {/* Created date column */}
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'created'}
                                            direction={sortBy === 'created' ? sortOrder : 'desc'}
                                            onClick={() => {
                                                const newOrder =
                                                    sortBy === 'created' && sortOrder === 'desc' ? 'asc' : 'desc';
                                                setSortBy('created');
                                                setSortOrder(newOrder);
                                                refetch({
                                                    eventId,
                                                    role,
                                                    status,
                                                    conditions: {
                                                        limit: pageSize === -1 ? -1 : pageSize,
                                                        offset: pageSize === -1 ? 0 : page * pageSize,
                                                        text: search,
                                                        orderBy: 'created',
                                                        order: newOrder,
                                                        workshopFilter,
                                                        regionFilter,
                                                    },
                                                });
                                            }}
                                        >
                                            Created
                                        </TableSortLabel>
                                    </TableCell>

                                    {/* Registration Link column */}
                                    <TableCell>Registration Link</TableCell>

                                    {/* Dynamic form fields with proper widths */}
                                    {data?.form?.fields?.map(field => {
                                        // Define column widths based on field names
                                        const getColumnWidth = fieldName => {
                                            const name = fieldName.toLowerCase();
                                            if (name.includes('Email')) return 160;
                                            if (
                                                name.includes('Name') ||
                                                name.includes('Designation') ||
                                                name.includes('job') ||
                                                name.includes('organization') ||
                                                name.includes('organization_name') ||
                                                name.includes('workshop') ||
                                                name.includes('company')
                                            )
                                                return 160;
                                            if (name.includes('country')) return 120;
                                            if (name.includes('calling_code')) return 120;
                                            return 180; // Default width for other fields
                                        };

                                        return (
                                            <TableCell
                                                key={field.name}
                                                sx={{
                                                    minWidth: getColumnWidth(field.name),
                                                    width: getColumnWidth(field.name),
                                                    maxWidth: getColumnWidth(field.name),
                                                }}
                                            >
                                                <TableSortLabel
                                                    active={sortBy === field.name}
                                                    direction={sortBy === field.name ? sortOrder : 'asc'}
                                                    onClick={() => {
                                                        const newOrder =
                                                            sortBy === field.name && sortOrder === 'asc'
                                                                ? 'desc'
                                                                : 'asc';
                                                        setSortBy(field.name);
                                                        setSortOrder(newOrder);
                                                        refetch({
                                                            eventId,
                                                            role,
                                                            status,
                                                            conditions: {
                                                                limit: pageSize === -1 ? -1 : pageSize,
                                                                offset: pageSize === -1 ? 0 : page * pageSize,
                                                                text: search,
                                                                orderBy: field.name,
                                                                order: newOrder,
                                                                workshopFilter,
                                                                regionFilter,
                                                            },
                                                        });
                                                    }}
                                                >
                                                    {field.name}
                                                </TableSortLabel>
                                            </TableCell>
                                        );
                                    })}

                                    {/* Conference Choices columns (only if feature is enabled) */}
                                    {isConferenceChoicesEnabled && (
                                        <>
                                            <TableCell sx={{ minWidth: 200, width: 200 }}>Venue Location</TableCell>
                                            <TableCell sx={{ minWidth: 180, width: 180 }}>Event Date From</TableCell>
                                            <TableCell sx={{ minWidth: 180, width: 180 }}>Event Date To</TableCell>
                                            <TableCell sx={{ minWidth: 200, width: 200 }}>Final Choice</TableCell>
                                            <TableCell sx={{ minWidth: 150, width: 150 }}>
                                                Confirmation Status
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.verifiedApplicants?.map((row, index) => {
                                    const parsedData = JSON.parse(row.data || '{}');
                                    const isVerified = row.is_choice_final === true;
                                    const isSelected = selectedIds.some(selected => selected.id === row.id);
                                    // Debug: Log row status to help troubleshoot
                                    if (index === 0) {
                                        console.log('First row status:', row.status, 'Row data:', row);
                                    }

                                    return (
                                        <TableRow
                                            key={row.id}
                                            hover
                                            selected={isSelected}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            {/* Checkbox cell */}
                                            {(permissions.includes('admin') || permissions.includes('email')) && (
                                                <TableCell padding='checkbox'>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={event => {
                                                            if (event.target.checked) {
                                                                setSelectedIds(prev => [
                                                                    ...prev,
                                                                    {
                                                                        id: row.id,
                                                                        email: parsedData.email || parsedData.Email,
                                                                    },
                                                                ]);
                                                            } else {
                                                                setSelectedIds(prev =>
                                                                    prev.filter(item => item.id !== row.id)
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </TableCell>
                                            )}

                                            {/* Actions cell */}
                                            <TableCell>
                                                <Stack direction='row' spacing={1}>
                                                    {/* Delete action */}
                                                    {permissions.includes('del-' + role) && (
                                                        <Tooltip title='Delete'>
                                                            <IconButton
                                                                size='small'
                                                                color='error'
                                                                onClick={() => {
                                                                    if (
                                                                        !window.confirm(
                                                                            'Are you sure you want to delete this?'
                                                                        )
                                                                    ) {
                                                                        return;
                                                                    }
                                                                    doDelete({
                                                                        variables: { eventId, role, id: row.id },
                                                                    })
                                                                        .catch(e => window.alert(e.message))
                                                                        .then(r => window.alert(r.data.result));
                                                                }}
                                                            >
                                                                <DeleteTwoTone />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Verified indicator - show when final_choice is true */}
                                                    {isVerified && (
                                                        <Tooltip title='Verified - Final Choice Confirmed'>
                                                            <IconButton size='small' color='success'>
                                                                <CheckCircle />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Accept/Reject actions */}
                                                    {/* Always show accept/reject buttons on VerificationPage - this is the core functionality */}
                                                    {/* Accept button - show when status is pending, reject, or undefined */}
                                                    {(row.status === 'pending' ||
                                                        row.status === 'reject' ||
                                                        !row.status ||
                                                        row.status === null) && (
                                                        <Tooltip title='Accept Registration'>
                                                            <IconButton
                                                                size='small'
                                                                color='success'
                                                                onClick={() => handleAcceptReject(row, 'accept')}
                                                            >
                                                                <ThumbUpOffAltIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {/* Reject button - show when status is pending, accept, or undefined */}
                                                    {(row.status === 'pending' ||
                                                        row.status === 'accept' ||
                                                        !row.status ||
                                                        row.status === null) && (
                                                        <Tooltip title='Reject Registration'>
                                                            <IconButton
                                                                size='small'
                                                                color='error'
                                                                onClick={() => handleAcceptReject(row, 'reject')}
                                                            >
                                                                <ThumbDownOffAltIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}

                                                    {/* Upgrade action */}
                                                    {permissions.includes('admin') &&
                                                        permissions.includes('upgrade-registration') &&
                                                        role === 'Corporate' &&
                                                        row.status === 'accept' && (
                                                            <Tooltip title='Upgrade Registration Limit'>
                                                                <IconButton
                                                                    size='small'
                                                                    color='success'
                                                                    onClick={() => handleAcceptReject(row, 'upgrade')}
                                                                >
                                                                    <Upgrade />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                </Stack>
                                            </TableCell>

                                            {/* ID cell */}
                                            <TableCell sx={{ minWidth: 100, width: 100 }}>{row.id}</TableCell>

                                            {/* Created cell */}
                                            <TableCell sx={{ minWidth: 160, width: 160 }}>
                                                {(() => {
                                                    try {
                                                        // Handle different timestamp formats
                                                        let timestamp = row.created;

                                                        // If it's a string that looks like a timestamp, convert to number
                                                        if (typeof timestamp === 'string' && /^\d+$/.test(timestamp)) {
                                                            timestamp = parseInt(timestamp, 10);
                                                        }

                                                        // If timestamp is too large (future date), it might be in milliseconds
                                                        // Convert to seconds if it's a future date
                                                        if (timestamp > 2000000000) {
                                                            // Year 2033 in seconds
                                                            timestamp = Math.floor(timestamp / 1000);
                                                        }

                                                        const date = new Date(timestamp * 1000);
                                                        if (isNaN(date.getTime())) {
                                                            return 'Invalid Date';
                                                        }
                                                        return format(date, 'dd-MMM-yyyy hh:mm a');
                                                    } catch (error) {
                                                        console.error(
                                                            'Date formatting error:',
                                                            error,
                                                            'Original value:',
                                                            row.created
                                                        );
                                                        return 'Invalid Date';
                                                    }
                                                })()}
                                            </TableCell>

                                            {/* Registration Link cell */}
                                            <TableCell sx={{ minWidth: 200, width: 200 }}>
                                                <Typography
                                                    variant='body2'
                                                    sx={{
                                                        maxWidth: 200,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {row.regLink}
                                                </Typography>
                                            </TableCell>

                                            {/* Dynamic form field cells with proper widths */}
                                            {data?.form?.fields?.map(field => {
                                                // Define column widths based on field names
                                                const getColumnWidth = fieldName => {
                                                    const name = fieldName.toLowerCase();
                                                    if (name.includes('email')) return 160;
                                                    if (
                                                        name.includes('name') ||
                                                        name.includes('designation') ||
                                                        name.includes('company')
                                                    )
                                                        return 160;
                                                    if (name.includes('country')) return 100;
                                                    if (name.includes('calling_code')) return 80;
                                                    return 100; // Default width for other fields
                                                };

                                                const columnWidth = getColumnWidth(field.name);

                                                return (
                                                    <TableCell
                                                        key={field.name}
                                                        sx={{
                                                            minWidth: columnWidth,
                                                            width: columnWidth,
                                                            maxWidth: columnWidth,
                                                        }}
                                                    >
                                                        {permissions.includes('edit-' + role) ? (
                                                            <TextField
                                                                size='small'
                                                                fullWidth
                                                                value={
                                                                    editValues[`${row.id}-${field.name}`] !== undefined
                                                                        ? editValues[`${row.id}-${field.name}`]
                                                                        : parsedData[field.name] || ''
                                                                }
                                                                onChange={e => {
                                                                    // Update local edit state
                                                                    setEditValues(prev => ({
                                                                        ...prev,
                                                                        [`${row.id}-${field.name}`]: e.target.value,
                                                                    }));
                                                                }}
                                                                onKeyDown={e => {
                                                                    if (e.key === 'Enter') {
                                                                        const newValue =
                                                                            editValues[`${row.id}-${field.name}`] ||
                                                                            e.target.value;
                                                                        updateField({
                                                                            variables: {
                                                                                eventId,
                                                                                role,
                                                                                id: row.id,
                                                                                field: field.name,
                                                                                value: newValue,
                                                                            },
                                                                        })
                                                                            .then(r => {
                                                                                setSnackbar({
                                                                                    open: true,
                                                                                    message:
                                                                                        r?.data?.result ||
                                                                                        'Updated successfully',
                                                                                    severity: 'success',
                                                                                });
                                                                                // Clear the edit value after successful update
                                                                                setEditValues(prev => {
                                                                                    const newEditValues = { ...prev };
                                                                                    delete newEditValues[
                                                                                        `${row.id}-${field.name}`
                                                                                    ];
                                                                                    return newEditValues;
                                                                                });
                                                                                refetch({
                                                                                    eventId: eventId || '7493635113',
                                                                                    role: role || 'participant',
                                                                                    status,
                                                                                    conditions: {
                                                                                        limit:
                                                                                            pageSize === -1
                                                                                                ? -1
                                                                                                : pageSize,
                                                                                        offset:
                                                                                            pageSize === -1
                                                                                                ? 0
                                                                                                : page * pageSize,
                                                                                        text: search,
                                                                                        orderBy: 'created',
                                                                                        order: 'desc',
                                                                                        workshopFilter,
                                                                                        regionFilter,
                                                                                        venueFilter,
                                                                                    },
                                                                                }); // Refresh data
                                                                            })
                                                                            .catch(error => {
                                                                                setSnackbar({
                                                                                    open: true,
                                                                                    message:
                                                                                        error.message ||
                                                                                        'Update failed',
                                                                                    severity: 'error',
                                                                                });
                                                                            });
                                                                    }
                                                                }}
                                                                placeholder={`Enter ${field.name}`}
                                                                sx={{
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '0.875rem',
                                                                        padding: '4px 8px',
                                                                    },
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography
                                                                variant='body2'
                                                                sx={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: columnWidth - 16, // Account for padding
                                                                }}
                                                            >
                                                                {parsedData[field.name] || '-'}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}

                                            {/* Conference Choices cells (only if feature is enabled) */}
                                            {isConferenceChoicesEnabled && (
                                                <>
                                                    <TableCell sx={{ minWidth: 200, width: 200 }}>
                                                        <Typography variant='body2'>
                                                            {row.venue_location || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 180, width: 180 }}>
                                                        <Typography variant='body2'>
                                                            {row.event_date_from
                                                                ? (() => {
                                                                      try {
                                                                          // Handle different timestamp formats
                                                                          let timestamp = row.event_date_from;
                                                                          if (
                                                                              typeof timestamp === 'string' &&
                                                                              /^\d+$/.test(timestamp)
                                                                          ) {
                                                                              timestamp = parseInt(timestamp, 10);
                                                                          }
                                                                          // If timestamp is too large, convert from milliseconds to seconds
                                                                          if (timestamp > 2000000000) {
                                                                              timestamp = Math.floor(timestamp / 1000);
                                                                          }
                                                                          const date = new Date(timestamp * 1000);
                                                                          if (isNaN(date.getTime()))
                                                                              return row.event_date_from;
                                                                          return format(date, 'dd-MMM-yyyy');
                                                                      } catch (error) {
                                                                          return row.event_date_from;
                                                                      }
                                                                  })()
                                                                : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 180, width: 180 }}>
                                                        <Typography variant='body2'>
                                                            {row.event_date_to
                                                                ? (() => {
                                                                      try {
                                                                          // Handle different timestamp formats
                                                                          let timestamp = row.event_date_to;
                                                                          if (
                                                                              typeof timestamp === 'string' &&
                                                                              /^\d+$/.test(timestamp)
                                                                          ) {
                                                                              timestamp = parseInt(timestamp, 10);
                                                                          }
                                                                          // If timestamp is too large, convert from milliseconds to seconds
                                                                          if (timestamp > 2000000000) {
                                                                              timestamp = Math.floor(timestamp / 1000);
                                                                          }
                                                                          const date = new Date(timestamp * 1000);
                                                                          if (isNaN(date.getTime()))
                                                                              return row.event_date_to;
                                                                          return format(date, 'dd-MMM-yyyy');
                                                                      } catch (error) {
                                                                          return row.event_date_to;
                                                                      }
                                                                  })()
                                                                : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 200, width: 200 }}>
                                                        <Typography variant='body2'>
                                                            {row.final_choice || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 150, width: 150 }}>
                                                        <Chip
                                                            label={row.is_choice_final ? 'Finalized' : 'Pending'}
                                                            color={row.is_choice_final ? 'success' : 'warning'}
                                                            size='small'
                                                        />
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    );
                                })}

                                {/* Empty state */}
                                {(!data?.verifiedApplicants || data.verifiedApplicants.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                                            <Typography variant='body1' color='text.secondary'>
                                                No data available
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Enhanced Pagination with "All" option */}
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
                        component='div'
                        count={data?.total || 0}
                        rowsPerPage={pageSize === -1 ? data?.total || 0 : pageSize}
                        page={page}
                        onPageChange={(event, newPage) => {
                            setPage(newPage);
                        }}
                        onRowsPerPageChange={event => {
                            const newPageSize = parseInt(event.target.value, 10);
                            setPageSize(newPageSize);
                            setPage(0); // Always reset to first page when changing page size
                        }}
                        labelRowsPerPage='Rows per page:'
                        labelDisplayedRows={({ from, to, count }) => {
                            if (pageSize === -1) {
                                return `All ${count} rows (${data?.verifiedApplicants?.length || 0} displayed)`;
                            }
                            const totalPages = Math.ceil(count / pageSize);
                            return `${from}-${to} of ${count} (Page ${page + 1} of ${totalPages})`;
                        }}
                    />
                </Paper>
            </Box>
        </Stack>
    );
}

export const GET_VERIFIED_DATA = gql`
    query ($eventId: String!, $role: ROLE!, $status: STATUS, $conditions: InputConditions) {
        verifiedApplicants(eventId: $eventId, role: $role, status: $status, conditions: $conditions) {
            serial
            id
            created
            regLink
            data
            status
            venue_location
            event_date_from
            event_date_to
            is_choice_final
            final_choice
            isConfirmed
        }
        total: verifiedApplicantsCount(eventId: $eventId, role: $role, status: $status, conditions: $conditions)

        form(eventId: $eventId, role: $role) {
            fields {
                name
            }
            features
        }
    }
`;

const UPDATE_APPLICANT_FIELD = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!, $field: String!, $value: String!) {
        result: updateApplicantField(eventId: $eventId, role: $role, id: $id, field: $field, value: $value)
    }
`;
const UPDATE_APPLICANT_STATUS_AND_MAIL = gql`
    mutation (
        $eventId: String!
        $role: ROLE!
        $id: String!
        $field: String!
        $value: String!
        $no_of_participant: String
        $paidSeats: String
        $freeSeats: String
        $discount: String
    ) {
        result: updateApplicantStatusandMail(
            eventId: $eventId
            role: $role
            id: $id
            field: $field
            value: $value
            no_of_participant: $no_of_participant
            paidSeats: $paidSeats
            freeSeats: $freeSeats
            discount: $discount
        )
    }
`;

const DELETE_APPLICANT = gql`
    mutation ($eventId: String!, $role: ROLE!, $id: String!) {
        result: deleteApplicant(eventId: $eventId, role: $role, id: $id)
    }
`;

const GET_ME = gql`
    query {
        me {
            permissions
        }
    }
`;

const ASSIGN_VENUE = gql`
    mutation AssignVenue(
        $eventId: String!
        $role: ROLE!
        $ids: [String!]!
        $venueLocation: String!
        $eventDateFrom: String!
        $eventDateTo: String!
        $finalChoice: String!
        $status: String!
    ) {
        assignVenue(
            eventId: $eventId
            role: $role
            ids: $ids
            venueLocation: $venueLocation
            eventDateFrom: $eventDateFrom
            eventDateTo: $eventDateTo
            finalChoice: $finalChoice
            status: $status
        ) {
            success
            message
            updatedCount
        }
    }
`;
export const GET_VERIFICATION_FEATURE = gql`
    query ($eventId: String!, $role: ROLE!) {
        mailVerificationFeature(eventId: $eventId, role: $role)
    }
`;

const SEND_WHATSAPP_BULK_MESSAGE = gql`
    mutation SendWhatsAppBulkMessage($eventId: String!, $role: String!, $ids: [String!]!, $message: String) {
        sendWhatsAppBulkMessage(eventId: $eventId, role: $role, ids: $ids, message: $message) {
            success
            message
            successCount
            failedCount
            results {
                id
                phoneNumber
                success
                error
            }
        }
    }
`;

export function WhatsAppBulkMessage({ selectedIds, eventId, role }) {
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [results, setResults] = React.useState(null);
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

    const [sendWhatsAppBulkMessage] = useMutation(SEND_WHATSAPP_BULK_MESSAGE);

    const handleSend = async () => {
        if (!selectedIds.length) {
            setSnackbar({
                open: true,
                message: 'Please select recipients',
                severity: 'error',
            });
            return;
        }

        setLoading(true);
        try {
            const response = await sendWhatsAppBulkMessage({
                variables: {
                    eventId,
                    role,
                    ids: selectedIds.map(id => id.id),
                    message: message.trim() || undefined,
                },
            });

            const result = response.data.sendWhatsAppBulkMessage;

            setResults(result);
            setSnackbar({
                open: true,
                message: result.message,
                severity: result.success ? 'success' : 'warning',
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to send WhatsApp messages',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <AnimateButton>
                <Button
                    variant='contained'
                    color='success'
                    startIcon={<WhatsAppIcon />}
                    disabled={!selectedIds.length}
                    onClick={() => setOpen(true)}
                >
                    Send WhatsApp
                </Button>
            </AnimateButton>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth>
                <DialogTitle>Send WhatsApp Message</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Send WhatsApp messages to {selectedIds.length} selected recipient(s).
                        {message.trim()
                            ? ' A custom message will be included.'
                            : ' The default template message will be used.'}
                    </DialogContentText>

                    <TextField
                        sx={{ mt: 2 }}
                        label='Custom Message (Optional)'
                        multiline
                        rows={4}
                        fullWidth
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder='Enter an optional custom message...'
                    />

                    {results && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant='subtitle1' gutterBottom>
                                Results: {results.successCount} sent successfully, {results.failedCount} failed
                            </Typography>

                            {results.failedCount > 0 && (
                                <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                    <Typography variant='subtitle2'>Failed Messages:</Typography>
                                    {results.results
                                        .filter(r => !r.success)
                                        .map((result, index) => (
                                            <Typography key={index} variant='body2' color='error'>
                                                {result.phoneNumber || result.id}: {result.error}
                                            </Typography>
                                        ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSend} variant='contained' color='success' disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Send Messages'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
